import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DollarSign, Plus, List, CheckSquare, BarChart3, Receipt, ArrowLeft } from 'lucide-react';
import AddExpense from './expenses/AddExpense';
import ExpenseList from './expenses/ExpenseList';
import { formatCurrency } from '../../utils/helpers';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

const ExpenseManagement = ({
  games,
  tournaments,
  teams = [],
  expenses = [], // Default to empty array 
  onAddExpense,
  onApproveExpense,
  onDenyExpense,
  onDeleteExpense,
  onBack,
  currentUser,
  isTreasurer,
  clubId,
  clubName,
  players = [],
  onSettleTournamentFee
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('expenses');
  const [editingExpense, setEditingExpense] = useState(null);

  const handleUpdateExpense = async (updatedData) => {
    try {
      const expenseRef = doc(db, 'expenses', updatedData.id);
      await updateDoc(expenseRef, updatedData);
      setEditingExpense(null);
      handleTabChange('expenses');
    } catch (err) {
      console.error('Error updating expense:', err);
      alert('Failed to update expense. Check console for details.');
    }
  };

  // Sync with URL params for deep linking
  useEffect(() => {
    if (searchParams.get('review') === 'true' && isTreasurer) {
      setActiveTab('review');
    } else if (searchParams.get('tab')) {
      setActiveTab(searchParams.get('tab'));
    }
  }, [searchParams, isTreasurer]);

  // Handle tab change and update URL
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Preserve other params but update tab
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    if (tab !== 'review') newParams.delete('review');
    setSearchParams(newParams);
  };

  // TEMPORARY FIX: Allowed Initial Donations to be visible in the list so the Treasurer/Admin can delete duplicates.
  // We can revert this later if you want them strictly separated.
  const relevantExpenses = expenses;

  const pendingExpenses = relevantExpenses.filter(e => e.approvalStatus === 'pending');
  const myExpenses = relevantExpenses.filter(e => e.addedByPlayerId === currentUser?.uid || e.paidByAdminName === currentUser?.displayName);
  const recentExpenses = [...relevantExpenses].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);

  // Stats
  const totalPendingAmount = pendingExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  const totalExpensesMonth = relevantExpenses
    .filter(e => {
      const d = new Date(e.date);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    })
    .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <button
            onClick={onBack}
            className="text-gray-500 hover:text-gray-900 mb-2 flex items-center gap-1"
          >
            <ArrowLeft size={16} /> Back to Dashboard
          </button>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Receipt className="text-orange-600" />
            Manage Expenses
          </h1>
          <p className="text-gray-600">Track reimbursements and club spending</p>
        </div>

        <button
          onClick={() => handleTabChange('add')}
          className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 shadow-sm font-semibold transition-colors"
        >
          <Plus size={20} />
          Add Expense
        </button>
      </div>

      {/* Stats Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-500 text-sm font-medium">Pending Approval</span>
            <div className="p-2 bg-yellow-100 rounded-lg">
              <CheckSquare size={20} className="text-yellow-600" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900">{pendingExpenses.length}</div>
          <div className="text-sm text-yellow-600 font-medium">
            {formatCurrency(totalPendingAmount)} total
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-500 text-sm font-medium">This Month</span>
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign size={20} className="text-green-600" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalExpensesMonth)}</div>
          <div className="text-sm text-gray-500">
            Total spending
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-500 text-sm font-medium">Total Expenses</span>
            <div className="p-2 bg-blue-100 rounded-lg">
              <List size={20} className="text-blue-600" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900">{relevantExpenses.length}</div>
          <div className="text-sm text-gray-500">
            Recorded to date
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6 -mb-px overflow-x-auto">

          <button
            onClick={() => handleTabChange('expenses')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${activeTab === 'expenses'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            All Expenses
          </button>
          {isTreasurer && (
            <button
              onClick={() => handleTabChange('review')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors flex items-center gap-2 ${activeTab === 'review'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Review Requests
              {pendingExpenses.length > 0 && (
                <span className="bg-red-100 text-red-600 text-xs px-2 py-0.5 rounded-full font-bold">
                  {pendingExpenses.length}
                </span>
              )}
            </button>
          )}

        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">


        {activeTab === 'expenses' && (
          <ExpenseList
            expenses={relevantExpenses}
            onEdit={(expense) => {
              setEditingExpense(expense);
              handleTabChange('edit');
            }}
            onDelete={onDeleteExpense}
            onApprove={onApproveExpense}
            onDeny={onDenyExpense}
            isTreasurer={isTreasurer}
            currentUser={currentUser}
            games={games}
            players={players}
            tournaments={tournaments}
            clubName={clubName}
            onSettleTournamentFee={onSettleTournamentFee}
          />
        )}

        {activeTab === 'review' && isTreasurer && (
          <div>
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h3 className="font-bold text-yellow-800 flex items-center gap-2">
                <AlertTriangle size={20} />
                Pending Approvals
              </h3>
              <p className="text-sm text-yellow-700 mt-1">
                These expenses have been submitted by players/admins and require Treasurer approval for reimbursement.
              </p>
            </div>
            <ExpenseList
              expenses={pendingExpenses}
              onEdit={(expense) => {
                setEditingExpense(expense);
                handleTabChange('edit');
              }}
              onApprove={onApproveExpense}
              onDeny={onDenyExpense}
              onDelete={onDeleteExpense}
              isTreasurer={isTreasurer}
              currentUser={currentUser}
              games={games}
              players={players}
              tournaments={tournaments}
              clubName={clubName}
              onSettleTournamentFee={onSettleTournamentFee}
            />
            {pendingExpenses.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200 mt-4">
                <CheckSquare size={48} className="mx-auto text-gray-300 mb-2" />
                <h3 className="text-gray-900 font-medium">All caught up!</h3>
                <p className="text-gray-500">No pending expenses to review.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'add' && (
          <AddExpense
            games={games}
            tournaments={tournaments}
            teams={teams}
            onBack={() => handleTabChange('expenses')}
            onAddExpense={async (data) => {
              await onAddExpense(data);
              handleTabChange('expenses');
            }}
            currentUser={currentUser}
            players={players}
          />
        )}
        
        {activeTab === 'edit' && editingExpense && (
          <AddExpense
            games={games}
            tournaments={tournaments}
            teams={teams}
            onBack={() => {
              setEditingExpense(null);
              handleTabChange('expenses');
            }}
            initialData={editingExpense}
            isEditMode={true}
            onUpdateExpense={handleUpdateExpense}
            onDeleteExpense={async () => {
              if (window.confirm('Are you sure you want to delete this expense? This cannot be undone.')) {
                await onDeleteExpense(editingExpense.id);
                setEditingExpense(null);
                handleTabChange('expenses');
              }
            }}
            currentUser={currentUser}
            players={players}
          />
        )}
      </div>
    </div>
  );
};

// Start of fix for lucide-react import in dashboard
import { AlertTriangle } from 'lucide-react';

export default ExpenseManagement;